import { z } from 'zod';

/** Public waiting list (waitlist_requests) and the domain sign-in gate. */

export const WaitlistStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export type WaitlistStatus = z.infer<typeof WaitlistStatusSchema>;

/**
 * Consumer / shared-mailbox providers. Approving one of these would open
 * self-registration to every user of that provider — so the admin screen
 * warns before approval. Not a hard block (a real customer might genuinely
 * use one), but never silent.
 */
export const SHARED_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'msn.com', 'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'aol.com', 'icloud.com',
  'me.com', 'mac.com', 'proton.me', 'protonmail.com', 'gmx.com', 'gmx.net',
  'mail.com', 'zoho.com', 'yandex.com', 'pm.me', 'fastmail.com',
]);

export function isSharedEmailDomain(domain: string): boolean {
  return SHARED_EMAIL_DOMAINS.has(domain.trim().toLowerCase());
}

/**
 * Public submission. The company domain is DERIVED server-side from the email
 * ending and never accepted from the client. `website` is a honeypot: humans
 * never see it, bots fill it, filled submissions are silently discarded.
 */
export const CreateWaitlistInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  companyName: z.string().trim().min(1).max(200),
  phone: z.string().trim().max(50).default(''),
  email: z.string().trim().toLowerCase().email().max(320),
  website: z.string().max(500).optional(),
});
export type CreateWaitlistInput = z.infer<typeof CreateWaitlistInputSchema>;

export const WaitlistRequestSchema = z.object({
  id: z.string(),
  name: z.string(),
  companyName: z.string(),
  phone: z.string(),
  email: z.string(),
  domain: z.string(),
  status: WaitlistStatusSchema,
  createdAt: z.string(),
  reviewedBy: z.string().nullable(),
  reviewedByName: z.string().nullable(),
  reviewedAt: z.string().nullable(),
});
export type WaitlistRequest = z.infer<typeof WaitlistRequestSchema>;
